import { useEffect, useState } from "react";
import { Trophy, Flame, Target } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/common/Avatar";

const TROPHY_IMG = "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwdHJvcGh5JTIwY3VwJTIwY2hhbXBpb25zaGlwfGVufDB8fHx8MTc4NzU0NjAzMHww&ixlib=rb-4.1.0&q=85";

export default function LeaderboardPage() {
  const [rows, setRows] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/leaderboard").then((r) => setRows(r.data));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 tile p-6 grain">
          <div className="section-label">Global rankings</div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-1 flex items-center gap-3">
            <Trophy className="w-9 h-9 text-amber-400" /> The Ladder
          </h1>
          <p className="text-slate-400 mt-3 max-w-lg">
            Runs bucket correct → +10 pts. Wicket call correct → +8 pts. Full combo → +12 bonus. Streak up, climb up.
          </p>
        </div>
        <div className="tile p-0 overflow-hidden relative min-h-[180px]">
          <img src={TROPHY_IMG} alt="trophy" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1623] via-[#0F1623]/70 to-transparent" />
          <div className="relative p-6 flex flex-col justify-end h-full min-h-[180px]">
            <div className="section-label">Top slot</div>
            <div className="font-display font-black text-2xl uppercase mt-1">
              {rows[0]?.name || "Awaiting champion"}
            </div>
            <div className="font-mono-data text-amber-400 text-3xl mt-1">{rows[0]?.points ?? 0} pts</div>
          </div>
        </div>
      </div>

      <div className="tile p-0 overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 border-b border-white/5 section-label">
          <div className="col-span-1">#</div>
          <div className="col-span-5">Player</div>
          <div className="col-span-2 text-right">Accuracy</div>
          <div className="col-span-2 text-right">Streak</div>
          <div className="col-span-2 text-right">Points</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-slate-400 text-center" data-testid="leaderboard-empty">No predictions ranked yet. Be the first — pick an over.</div>
        ) : rows.map((row) => (
          <div
            key={row.id}
            className={`grid grid-cols-12 items-center px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] ${user?.id === row.id ? "bg-amber-500/5" : ""}`}
            data-testid={`leaderboard-row-${row.rank}`}
          >
            <div className="col-span-1">
              <RankPill rank={row.rank} />
            </div>
            <div className="col-span-5 flex items-center gap-3">
              <Avatar userId={row.id} name={row.name} hasAvatar={row.has_avatar} version={row.avatar_version} size={36} />
              <div>
                <div className="font-semibold">{row.name}{user?.id === row.id ? <span className="ml-2 text-amber-400 text-xs uppercase tracking-wider">You</span> : null}</div>
                <div className="text-xs text-slate-500 font-mono-data">{row.correct_predictions}/{row.total_predictions} correct</div>
              </div>
            </div>
            <div className="col-span-2 text-right font-mono-data text-emerald-400 flex items-center justify-end gap-1"><Target className="w-3.5 h-3.5" />{row.accuracy}%</div>
            <div className="col-span-2 text-right font-mono-data text-amber-400 flex items-center justify-end gap-1"><Flame className="w-3.5 h-3.5" />{row.streak}</div>
            <div className="col-span-2 text-right metric-value text-white text-lg">{row.points}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RankPill({ rank }) {
  const colors = rank === 1 ? "bg-amber-500/20 text-amber-300 border-amber-500/40" :
                 rank === 2 ? "bg-slate-300/20 text-slate-200 border-slate-300/30" :
                 rank === 3 ? "bg-orange-600/20 text-orange-300 border-orange-600/40" :
                 "bg-slate-800 text-slate-400 border-slate-700";
  return <div className={`w-8 h-8 rounded-lg border flex items-center justify-center font-mono-data font-bold ${colors}`}>{rank}</div>;
}
